import { Box, Loader, Stack } from '@mantine/core';
import { RevisionGetArchiveClauseHistoryResponse } from 'api/actions/revision-get-archive-clause-history/revision-get-archive-clause-history-response';
import { useApi } from 'api/api-context';
import Toast from 'components/Toast';
import FilePreview from 'components/files/FilePreview';
import Slider from 'components/modals/slider/Slider';
import H3Medium from 'components/typography/H3Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import ReadonlyText from 'components/typography/ReadonlyText';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface RevisionArchiveClauseHistoryProps {
  revisionId: number;
}

/** Displays retained archive-clause versions preceding the current version. */
export default function RevisionArchiveClauseHistory({ revisionId }: RevisionArchiveClauseHistoryProps) {
  const { getAction } = useApi();
  const { isMobile, isTablet } = useResponsiveDesign();
  const [history, setHistory] = useState<RevisionGetArchiveClauseHistoryResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [openedSlider, setOpenSlider] = useState(false);
  const [initialFileId, setInitialFileId] = useState('');

  useEffect(() => {
    setHistory(null);
    setFailed(false);

    getAction('RevisionGetArchiveClauseHistory')({ parameters: { revisionId: String(revisionId) } })
      .then(setHistory)
      .catch((error) => {
        setFailed(true);
        panic(error);
      });
  }, [revisionId]);

  const previousVersions = useMemo(() => history?.filter(({ isCurrent }) => !isCurrent) ?? [], [history]);

  const handleOpenSlider = useCallback((fileId: string) => {
    setInitialFileId(fileId);
    setOpenSlider(true);
  }, []);

  return (
    <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
      <H3Medium color="gray.7">História podpisových doložiek</H3Medium>

      {!history && !failed && <Loader size="sm" aria-label="Načítavam históriu podpisových doložiek" />}

      {failed && (
        <Toast
          type="fail"
          message="Históriu podpisových doložiek sa nepodarilo načítať"
          withCloseButton={false}
          fullWidth
        />
      )}

      {history && previousVersions.length === 0 && <P2Regular>Žiadne staršie podpisové doložky.</P2Regular>}

      {previousVersions.length > 0 &&
        (isMobile ? (
          <Stack spacing={0}>
            {previousVersions.map((version) => (
              <Stack
                key={version.revisionArchiveClauseId}
                spacing={12}
                py={16}
                px={16}
                sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}` })}
              >
                <ReadonlyText title="Dátum" value={new Date(version.recordedAt).toLocaleString()} />
                <ReadonlyText
                  title="Podpisová doložka"
                  value={
                    <FilePreview
                      fileId={version.fileId}
                      size="sm"
                      hideDelete
                      excerptLength={100}
                      showSlider
                      onOpenSlider={() => handleOpenSlider(version.fileId)}
                    />
                  }
                />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={0}>
            <Box className="grid grid-cols-[20%_1fr] rounded-[4px]" p={16} bg="gray.0">
              <P1Medium>Dátum</P1Medium>
              <P1Medium>Podpisová doložka</P1Medium>
            </Box>
            {previousVersions.map((version) => (
              <Box
                py={24}
                px={16}
                key={version.revisionArchiveClauseId}
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.colors.gray[1]}`,
                  alignItems: 'flex-start',
                })}
                className="grid grid-cols-[20%_1fr]"
              >
                <P2Regular>{new Date(version.recordedAt).toLocaleString()}</P2Regular>
                <FilePreview
                  fileId={version.fileId}
                  size="sm"
                  hideDelete
                  excerptLength={100}
                  showSlider
                  onOpenSlider={() => handleOpenSlider(version.fileId)}
                />
              </Box>
            ))}
          </Stack>
        ))}

      <Slider
        key={initialFileId}
        opened={openedSlider}
        fileIds={[initialFileId]}
        onClose={() => setOpenSlider(false)}
        initialImageId={initialFileId}
        hideControls={true}
      />
    </Stack>
  );
}
